import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, PhoneCall, Headphones, Workflow, CheckCircle2 } from 'lucide-react';
import ThemeToggle from './ThemeToggle';

const tabs = [
  { id: 'leadgen', label: 'Lead Gen', icon: Sparkles },
  { id: 'voice', label: 'Voice AI', icon: PhoneCall },
  { id: 'support', label: 'Customer Support', icon: Headphones },
  { id: 'workflow', label: 'Workflow Automation', icon: Workflow },
];

const trustedLogos = [
  'KOGNITA',
  'OZE GROWTH',
  'BLDG',
];

export default function Hero() {
  const [activeTab, setActiveTab] = useState('leadgen');
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab((current) => {
        const currentIndex = tabs.findIndex(t => t.id === current);
        return tabs[(currentIndex + 1) % tabs.length].id;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
    <section className="relative px-6 pt-[clamp(4.5rem,9vh,5.5rem)] pb-[clamp(1rem,3vh,1.5rem)] max-w-7xl mx-auto text-center w-full h-[100dvh] flex flex-col justify-center overflow-hidden">

      {/* Navigation (Absolute top) */}
      <nav className="absolute top-0 left-0 right-0 px-4 md:px-6 py-4 flex justify-between items-center max-w-7xl mx-auto w-full animate-fade-in-up z-50" style={{ animationDelay: '0.1s' }}>
        <Link to="/" className="flex items-center hover:opacity-80 transition-opacity" aria-label="DAIEN - strona główna">
          <img src="/logoDaien.svg" alt="DAIEN" className="w-8 h-8 md:w-9 md:h-9" width="36" height="36" />
        </Link>

        <div className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
          <Link to="/uslugi" className="text-sm text-muted hover:text-text-primary transition-colors">
            Usługi
          </Link>
          <Link to="/realizacje" className="text-sm text-muted hover:text-text-primary transition-colors">
            Realizacje
          </Link>
          <a href="#about" className="text-sm text-muted hover:text-text-primary transition-colors">O mnie</a>
          <a href="#cennik" className="text-sm text-muted hover:text-text-primary transition-colors">Cennik</a>
          <Link to="/blog" className="text-sm text-muted hover:text-text-primary transition-colors">Blog</Link>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <ThemeToggle />
          <a
            href="#kontakt"
            className="bg-text-primary text-bg px-4 md:px-5 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-medium hover:bg-text-primary/90 whitespace-nowrap btn-animated"
          >
            Umów rozmowę
          </a>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="flex flex-col items-center px-2 relative z-10 w-full gap-[clamp(0.6rem,1.8vh,1.25rem)]">

        <h1 className="text-[clamp(1.85rem,5.2vw,4rem)] [@media(max-height:760px)]:text-[clamp(1.6rem,4.2vw,2.75rem)] [@media(max-height:640px)]:text-[clamp(1.35rem,3.6vw,2.1rem)] font-normal leading-[1.1] tracking-tight max-w-6xl mx-auto text-balance animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          Wygeneruję twojej firmie więcej zleceń,{' '}
          <span className="bg-gradient-to-r from-[hsl(var(--accent-from))] via-[hsl(var(--accent-to))] to-text-primary bg-clip-text text-transparent block sm:inline mt-1 sm:mt-0">
            bez chaosu i z mierzalnymi wynikami.
          </span>
        </h1>

        <p className="text-[clamp(0.82rem,1.6vw,1.125rem)] text-muted max-w-3xl mx-auto animate-fade-in-up text-balance px-4" style={{ animationDelay: '0.4s' }}>
          Jeśli jesteś właścicielem firmy usługowej i czujesz, że zostajesz w tyle z technologią oraz ze zleceniami, to wypełnij quiz i dowiedz się, jak możemy Ci pomóc.
        </p>

        <button
          onClick={() => navigate('/quiz')}
          className="bg-text-primary text-bg px-6 md:px-8 py-3 rounded-full text-sm md:text-base font-medium animate-fade-in-up w-full max-w-xs sm:w-auto btn-animated"
          style={{ animationDelay: '0.5s' }}
        >
          Wypełnij quiz kwalifikacyjny
        </button>

        {/* Tab Bar */}
        <div className="bg-surface border border-stroke rounded-xl p-1.5 w-full max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <div className="grid grid-cols-2 md:flex md:flex-row items-center justify-between gap-1">
            {tabs.map((tab, idx) => (
              <div key={tab.id} className="flex items-center w-full">
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-bg text-text-primary shadow-sm border border-stroke'
                      : 'text-muted hover:text-text-primary'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
                {idx < tabs.length - 1 && <div className="hidden md:block w-px h-5 bg-stroke mx-1"></div>}
              </div>
            ))}
          </div>
        </div>

        {/* Brand Background + Overlay Section */}
        <div className="relative rounded-3xl overflow-hidden h-[clamp(232px,32vh,320px)] w-full animate-fade-in-up max-w-5xl mx-auto border border-stroke" style={{ animationDelay: '0.7s' }}>
          <img
            srcSet="/hero-meadow-poster-mobile.webp 800w, /hero-meadow-poster.webp 1280w"
            sizes="(max-width: 768px) 100vw, 1200px"
            src="/hero-meadow-poster.webp"
            alt="DAIEN - systemy AI dla firm uslugowych"
            aria-hidden="true"
            // @ts-expect-error fetchpriority not yet in React 18 typings
            fetchpriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover z-0"
            width="1280"
            height="720"
          />
          <video
            className="absolute inset-0 w-full h-full object-cover z-[1]"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
          >
            <source src="/hero-meadow.webm" type="video/webm" />
            <source src="/hero-meadow.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-bg/10 z-[2]"></div>

          {/* Conditional Overlays */}
          <div className="absolute inset-0 flex items-center justify-center p-4 z-10">
            {activeTab === 'leadgen' && (
              <div className="animate-fade-in-overlay absolute inset-0 flex items-center justify-center">
                <div className="animate-slide-up-overlay bg-surface border border-stroke p-3 sm:p-4 md:p-5 rounded-2xl w-[88%] max-w-md max-h-full overflow-hidden shadow-2xl flex flex-col">
                  <p className="text-[clamp(0.95rem,2.4vw,1.125rem)] font-semibold mb-2 sm:mb-3 text-text-primary">Generowanie Leadów za pomocą AI</p>
                  <div className="w-full bg-bg rounded-full h-2 mb-2 sm:mb-3 overflow-hidden">
                    <div className="bg-accent-from h-2 rounded-full w-1/4"></div>
                  </div>
                  <p className="text-sm text-muted mb-2">25% gotowe</p>
                  <ul className="space-y-1.5 sm:space-y-2">
                    {['ICP + docelowy klient', 'Strona lądowania + monitoring ruchu', 'Kampanie reklamowe FB Ads', 'Ocena Potencjalnych Klientów'].map((step, i) => (
                      <li key={step} className="flex items-center gap-3 text-sm text-muted">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${i === 0 ? 'bg-accent-from text-bg' : 'bg-bg border border-stroke'}`}>
                          {i + 1}
                        </div>
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {activeTab === 'voice' && (
              <div className="animate-fade-in-overlay absolute inset-0 flex items-center justify-center">
                <div className="animate-slide-up-overlay bg-surface border border-stroke p-3 sm:p-4 md:p-5 rounded-2xl w-[88%] max-w-md max-h-full overflow-hidden shadow-2xl flex flex-col">
                  <p className="text-[clamp(0.95rem,2.4vw,1.125rem)] font-semibold mb-2 sm:mb-3 text-text-primary">AI Agent Głosowy</p>
                  <div className="w-full bg-bg rounded-full h-2 mb-2 sm:mb-3 overflow-hidden">
                    <div className="bg-accent-from h-2 rounded-full w-[67%]"></div>
                  </div>
                  <p className="text-sm text-muted mb-2 sm:mb-3">67% połączeń obsłużonych</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { l: 'Stosunek odebranych', v: '99.2%' },
                      { l: 'Umówioncyh spotkań', v: '34.7%' },
                      { l: 'Średni czas rozmowy', v: '2:14' },
                      { l: 'Oszczędność czasu/dzień', v: '4.5h' },
                    ].map((m) => (
                      <div key={m.l} className="bg-bg border border-stroke p-2 rounded-xl text-center">
                        <div className="text-xs text-muted mb-1">{m.l}</div>
                        <div className="text-[clamp(0.95rem,2.4vw,1.125rem)] font-semibold text-text-primary">{m.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'support' && (
              <div className="animate-fade-in-overlay absolute inset-0 flex items-center justify-center">
                <div className="animate-slide-up-overlay bg-surface border border-stroke p-3 sm:p-4 md:p-5 rounded-2xl w-[88%] max-w-md max-h-full overflow-hidden shadow-2xl flex flex-col items-center text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-accent-from/20 text-accent-from rounded-full flex items-center justify-center mb-2 sm:mb-3">
                    <CheckCircle2 className="w-6 h-6 sm:w-7 sm:h-7" />
                  </div>
                  <p className="text-[clamp(0.95rem,2.4vw,1.125rem)] font-semibold mb-2 text-text-primary">Customer Support</p>
                  <p className="text-sm text-muted mb-3 sm:mb-4">Obsluga klienta w czasie rzeczywistym</p>
                  <div className="bg-bg border border-stroke px-6 py-3 rounded-full text-sm font-medium text-text-primary">
                    do 87% msc.
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'workflow' && (
              <div className="animate-fade-in-overlay absolute inset-0 flex items-center justify-center">
                <div className="animate-slide-up-overlay bg-surface border border-stroke p-3 sm:p-4 md:p-5 rounded-2xl w-[88%] max-w-md max-h-full overflow-hidden shadow-2xl flex flex-col">
                  <p className="text-[clamp(0.95rem,2.4vw,1.125rem)] font-semibold mb-3 sm:mb-4 text-text-primary">Automatyzacje procesów</p>
                  <ul className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                    {['Łączenie twoich narzędzi', 'Automatyczne updaty CRM', 'Procesy oszczędzające Twój czas', 'Monitoring zwrotu inwestycji'].map((item) => (
                      <li key={item} className="flex items-center gap-3 text-sm text-muted">
                        <CheckCircle2 className="w-4 h-4 text-accent-from" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <button className="w-full bg-accent-to hover:bg-accent-from text-bg py-2.5 rounded-xl text-sm font-medium transition-colors">
                    Uruchom workflow
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </section>

    {/* Trusted By Logos - osobna sekcja pod fold */}
    <section className="px-6 py-12 max-w-7xl mx-auto w-full">
      <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
        {trustedLogos.map((logo) => (
          <span key={logo} className="text-base md:text-lg font-bold tracking-widest text-muted">
            {logo}
          </span>
        ))}
      </div>
    </section>
    </>
  );
}
